import updateCart from '~/firebase/update/updateCart';
import { useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import authAtom from '~/recoil/atom/auth';
import cartSelector from '~/recoil/selectors/cart';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useNavigate, useParams } from 'react-router-dom';

import useCartId from '~/hooks/useCartId';

import checkout from '~/restApi/checkout';
import paymentRedirect from '~/restApi/paymentRedirect';

import * as fp from 'lodash/fp';

import { PLATFORM_ID } from '~/globalVariables';
import { CartItem, CartStatus, CartType, MemberStoreCart } from '~/types/cart';
import { PaymentType } from '~/types/common';
import { StoreOrderInCart } from '~/types/cart';
import getPath from '~/utils/getNavigatePath';
import useEnterpriseGroupBuyCart from '~/hooks/useEnterpriseGroupBuyCart';
import globalEnterpriseGroupBuyAtom from '~/recoil/atom/enterpriseGroupBuy/globalEnterpriseGroupBuyAtom';
import cartStatus from '~/recoil/atom/cartStatus';

export enum ErrorType {
  NONE = 'NONE',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  INVALID_CART = 'INVALID_CART', //call api user !== cart owner
  CART_NOT_FOUND = 'CART_NOT_FOUND', // can't find cart doc
  INVALID_CART_STATUS = 'INVALID_CART_STATUS', // cart status !== ORDERING
  INVALID_ORDER_TYPE = 'INVALID_ORDER_TYPE', // cart order type not allow in platform
  NOT_ALLOW_MULTI_STORE = 'NOT_ALLOW_MULTI_STORE', // can't order more than 1 store (慈濟沒有這個ｅｒｒｏｒ)
  INVALID_STORE = 'INVALID_STORE', // store chose status is not OPEN
  INVALID_PAYMENT_TYPE = 'INVALID_PAYMENT_TYPE', // payment type not in platform
  INVALID_PICK_MEAL_POINT = 'INVALID_PICK_MEAL_POINT', // cart pick point 不存在或資訊錯誤（不是最新的
  INVALID_PICK_MEAL_TIME = 'INVALID_PICK_MEAL_TIME', // pickMealAt, pickMealEnd 不符合平台預定時間或取餐點時間段或店家預定時間
  INVALID_TAX_INVOICE = 'INVALID_TAX_INVOICE', // 統編不對
  INVALID_ITEM = 'INVALID_ITEM', // 品項或選項資訊不正確
  ITEM_NOT_IN_MENU = 'ITEM_NOT_IN_MENU', // 該品項不在菜單裡面
  INVALID_ITEM_INVENTORY = 'INVALID_ITEM_INVENTORY', // 有品項或選項完售或庫存不足
  SUBTOTAL_NOT_OVER_LIMIT = 'SUBTOTAL_NOT_OVER_LIMIT', // 沒有超過下單標準
  EMPTY_CART = 'EMPTY_CART', // 購物車是空的
  USER_NOT_HAVE_TZ_WALLET = 'USER_NOT_HAVE_TZ_WALLET', // 使用者沒有綁定慈濟錢包
  TZ_WALLET_NOT_ENOUGH = 'TZ_WALLET_NOT_ENOUGH', // 使用者慈濟錢包餘額不足,
  INVALID_EMAIL = 'INVALID_EMAIL',
  INVALID_COUPON = 'INVALID_COUPON',
}

const errorMapString: Record<ErrorType, { title: string; message: string }> = {
  [ErrorType.NONE]: { title: '', message: '' },
  [ErrorType.INTERNAL_SERVER_ERROR]: { title: '', message: '系統錯誤，請重試' },
  [ErrorType.INVALID_CART]: { title: '', message: '購物車不屬於使用者本人' },
  [ErrorType.CART_NOT_FOUND]: { title: '', message: '此購物車不存在' },
  [ErrorType.INVALID_CART_STATUS]: { title: '', message: '購物車狀態不符' },
  [ErrorType.INVALID_ORDER_TYPE]: { title: '', message: '此平台不支援此訂餐方式' },
  [ErrorType.NOT_ALLOW_MULTI_STORE]: { title: '', message: '系統錯誤，請重試' },
  [ErrorType.INVALID_STORE]: {
    title: '已過某些店家最後可接單的時間',
    message: '系統將顯示已過結單時間的店家，移除該店家品項即可下單，或是更改取餐時間',
  },
  [ErrorType.INVALID_PAYMENT_TYPE]: { title: '', message: '平台不支援此付款方式' },
  [ErrorType.INVALID_PICK_MEAL_POINT]: { title: '', message: '取餐點有誤或已不存在' },
  [ErrorType.INVALID_PICK_MEAL_TIME]: { title: '', message: '取餐時間不符' },
  [ErrorType.INVALID_TAX_INVOICE]: { title: '', message: '統編填寫錯誤' },
  [ErrorType.INVALID_ITEM]: { title: '', message: '品項或選項資訊不正確' },
  [ErrorType.ITEM_NOT_IN_MENU]: { title: '', message: '品項不在菜單內' },
  [ErrorType.INVALID_ITEM_INVENTORY]: { title: '', message: '有品項或選項完售或庫存不足' },
  [ErrorType.SUBTOTAL_NOT_OVER_LIMIT]: { title: '', message: '沒有超過下單標準' },
  [ErrorType.EMPTY_CART]: { title: '', message: '購物車是空的' },
  [ErrorType.USER_NOT_HAVE_TZ_WALLET]: { title: '', message: '使用者未綁定慈濟錢包' },
  [ErrorType.TZ_WALLET_NOT_ENOUGH]: { title: '', message: '慈濟錢包餘額不足' },
  [ErrorType.INVALID_EMAIL]: { title: '', message: '未輸入電子信箱' },
  [ErrorType.INVALID_COUPON]: { title: '', message: '優惠券不符' },
};

export default function useCheckout() {
  const auth = useRecoilValue(authAtom);
  const { enterpriseGroupBuyId = '', groupId = '' } = useParams();
  const { cartId, cartType } = useCartId();
  const cart = useRecoilValue(cartSelector);
  const setCartStatus = useSetRecoilState(cartStatus);
  const navigate = useNavigate();
  const { isOpen: isLoading, onOpen: onLoadingOpen, onClose: onLoadingClose } = useDisclosure();
  const {
    isOpen: isRedirecting,
    onOpen: onRedirectingOpen,
    onClose: onRedirectingClose,
  } = useDisclosure();
  const confirmModal = useDisclosure();

  const [checkoutError, setCheckoutError] = useState<{
    type: ErrorType;
    title: string;
    message: string;
  }>({
    type: ErrorType.NONE,
    title: '',
    message: '',
  });

  const [checkoutErrorOrders, setCheckoutErrorOrders] = useState<{
    type: ErrorType;
    invalidOrders: Array<StoreOrderInCart>;
    validOrders: Array<StoreOrderInCart>;
  }>({
    type: ErrorType.NONE,
    invalidOrders: [],
    validOrders: [],
  });

  // 店家取餐時間錯誤
  const [invalidStoreIds, setInvalidStoreIds] = useState<Array<string>>([]);

  const handleClearError = () => {
    setCheckoutError({
      type: ErrorType.NONE,
      message: '',
      title: '',
    });
  };

  const handleClearOrdersError = async () => {
    if (!cartId) return;

    try {
      await updateCart(PLATFORM_ID, cartId, {
        orders: checkoutErrorOrders.validOrders,
      });
      setCheckoutErrorOrders({
        type: ErrorType.NONE,
        invalidOrders: [],
        validOrders: [],
      });
    } catch (err) {
      console.error('update valid orders error', err);
    }
  };

  const handleCheckout = async () => {
    if (!auth) {
      return;
    }
    confirmModal.onClose();
    try {
      onLoadingOpen();
      await updateCart(PLATFORM_ID, cartId, {
        status: CartStatus.ORDERING,
      });
    } catch (err) {
      console.error('update status error');

      onLoadingClose();
      return;
    }

    try {
      const token = await auth.getIdToken();
      const res = await checkout(cartId, token);

      if (
        fp.includes(cart.paymentType)([
          PaymentType.PLATFORM,
          PaymentType.CASH,
          PaymentType.TZ_WALLET,
        ])
      ) {
        navigate(getPath('/orderList', groupId, enterpriseGroupBuyId));
      } else {
        onRedirectingOpen();
        paymentRedirect(res.orderId, token);
      }
    } catch (err: any) {
      if (err.response.data) {
        fp.cond<any, any>([
          [
            fp.where({
              code: fp.eq(ErrorType.INVALID_STORE),
            }),
            (errData) => {
              setInvalidStoreIds(errData?.stores || []);
            },
          ],
          [
            fp.where({
              code: fp.eq(ErrorType.INVALID_ITEM),
            }),
            (errData) => {
              const { validOrders, invalidOrders } = fp.eq(cartType, CartType.CART_GROUP)
                ? splitGroupOrders(cart.orders, errData.message || {})
                : splitOrders(cart.orders, errData.message || {});
              setCheckoutErrorOrders({
                type: errData.code as ErrorType,
                validOrders,
                invalidOrders,
              });
            },
          ],
          [
            fp.where({
              code: fp.eq(ErrorType.INVALID_ITEM_INVENTORY),
            }),
            (errData) => {
              const { validOrders, invalidOrders } = fp.eq(cartType, CartType.CART_GROUP)
                ? splitGroupOrders(cart.orders, errData.message || {})
                : splitOrders(cart.orders, errData.message || {});
              setCheckoutErrorOrders({
                type: errData.code as ErrorType,
                validOrders,
                invalidOrders,
              });
            },
          ],
          [
            fp.where({
              code: fp.T,
            }),
            (errData) => {
              setCheckoutError({
                type: errData.code as ErrorType,
                title: errorMapString[errData.code as ErrorType].title,
                message: errorMapString[errData.code as ErrorType].message,
              });
            },
          ],
        ])(err.response.data);
      }
    } finally {
      onLoadingClose();
    }
  };

  return {
    isLoading,
    isRedirecting,
    invalidStoreIds,
    checkoutError,
    checkoutErrorOrders,
    confirmModal,
    handleCheckout,
    handleClearError,
    handleClearOrdersError,
  };
}

// 切分合法與不合法的品項
function splitOrders(
  orders: Array<StoreOrderInCart>,
  errorItemsKeyId: Record<string, Array<string>>,
): { validOrders: Array<StoreOrderInCart>; invalidOrders: Array<StoreOrderInCart> } {
  return orders.reduce<{
    validOrders: Array<StoreOrderInCart>;
    invalidOrders: Array<StoreOrderInCart>;
  }>(
    (acc, order) => {
      const storeErrorItemsId = fp.propOr<Record<string, Array<string>>, string, Array<string>>(
        [],
        order.storeId,
      )(errorItemsKeyId);

      const [invalidItems = [], validItems = []] = fp.partition<CartItem>((item) =>
        storeErrorItemsId.includes(item.keyId),
      )(order.items);

      const invalidStoreOrder: StoreOrderInCart = fp.set('items', invalidItems)(order);
      const validStoreOrder: StoreOrderInCart = fp.set('items', validItems)(order);

      return {
        validOrders: fp.gt(validStoreOrder.items.length, 0)
          ? [...acc.validOrders, validStoreOrder]
          : acc.validOrders,
        invalidOrders: fp.gt(invalidStoreOrder.items.length, 0)
          ? [...acc.invalidOrders, invalidStoreOrder]
          : acc.invalidOrders,
      };
    },
    {
      invalidOrders: [],
      validOrders: [],
    },
  );
}

function splitGroupOrders(
  orders: Array<StoreOrderInCart>,
  errorItemsKeyId: Record<string, Array<string>>,
): { validOrders: Array<StoreOrderInCart>; invalidOrders: Array<StoreOrderInCart> } {
  return orders.reduce<{
    validOrders: Array<StoreOrderInCart>;
    invalidOrders: Array<StoreOrderInCart>;
  }>(
    (acc, order) => {
      const storeErrorItemsId = fp.propOr<Record<string, Array<string>>, string, Array<string>>(
        [],
        order.storeId,
      )(errorItemsKeyId);

      // split groupItems
      const [invalidGroupItems = [], validGroupItems = []] = order.groupItems.reduce<
        [Array<MemberStoreCart>, Array<MemberStoreCart>]
      >(
        ([invalidGroupItems, validGroupItems], groupItem) => {
          const [invalidItems = [], validItems = []] = splitItems(
            groupItem.items,
            storeErrorItemsId,
          );

          const invalidGroupItem = fp.set('items', invalidItems)(groupItem);
          const validGroupItem = fp.set('items', validItems)(groupItem);

          return [
            fp.gt(invalidGroupItem.items.length, 0)
              ? invalidGroupItems.concat([invalidGroupItem])
              : invalidGroupItems,
            fp.gt(validGroupItem.items.length, 0)
              ? validGroupItems.concat([validGroupItem])
              : validGroupItems,
          ];
        },
        [[], []],
      );

      const invalidOrders = fp.set('groupItems', invalidGroupItems)(order);
      const validOrders = fp.set('groupItems', validGroupItems)(order);

      return {
        validOrders: fp.gt(validGroupItems.length, 0)
          ? acc.validOrders.concat([validOrders])
          : acc.validOrders,
        invalidOrders: fp.gt(invalidGroupItems.length, 0)
          ? acc.invalidOrders.concat([invalidOrders])
          : acc.invalidOrders,
      };
    },
    { invalidOrders: [], validOrders: [] },
  );
}

function splitItems(
  items: Array<CartItem>,
  errorItemsKeyId: Array<string>,
): [Array<CartItem>, Array<CartItem>] {
  const errorItemKeyIdSet = new Set(errorItemsKeyId);
  return fp.partition<CartItem>((item) => errorItemKeyIdSet.has(item.keyId))(items);
}
